import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Heart,
  LogOut,
  Menu,
  MessagesSquare,
  Moon,
  Plus,
  Search,
  Settings,
  Sun,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth, useTheme } from '@/hooks/useApp';
import { useGuardedLink } from '@/hooks/useGuardedLink';
import { useSeoCatalogue } from '@/hooks/useSeo';
import { BUY_GUIDE_SLUG, SELL_GUIDE_SLUG, categoryPath } from '@/lib/seo';
import { Button, IconButton } from '@/components/ui';
import { NotificationBell } from '@/components/NotificationBell';
import { Logo } from '@/components/Logo';
import { cn } from '@/lib/cn';

/**
 * How many conversations are waiting.
 *
 * Its own endpoint and its own query, because this is polled far more often than the inbox is
 * opened and must never pay for a list of threads to answer with one number. React Query stops
 * polling when the tab is in the background, which is what makes a thirty-second interval
 * reasonable rather than rude.
 */
function useUnreadMessages() {
  const { user } = useAuth();

  const { data } = useQuery({
    queryKey: ['unread-messages'],
    queryFn: () => api<{ count: number }>('/api/messages/unread'),
    enabled: Boolean(user),
    refetchInterval: 30_000,
  });

  return data?.count ?? 0;
}

function Wordmark() {
  return (
    <Link
      to="/"
      className="group flex shrink-0 items-center"
      aria-label="KojaKola, početna strana"
    >
      <Logo className="h-8 sm:h-9" interactive />
    </Link>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();

  return (
    <IconButton
      label={theme === 'dark' ? 'Uključi svetlu temu' : 'Uključi tamnu temu'}
      onClick={toggle}
      className="relative overflow-hidden"
    >
      {/*
        Both icons stay mounted and cross-rotate. Swapping one for the other makes the control blink;
        rotating between them makes it feel like a physical switch.
      */}
      <Sun
        size={18}
        className={cn(
          'absolute transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
          theme === 'dark' ? 'rotate-0 scale-100 opacity-100' : 'rotate-90 scale-50 opacity-0',
        )}
      />
      <Moon
        size={18}
        className={cn(
          'absolute transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
          theme === 'dark' ? '-rotate-90 scale-50 opacity-0' : 'rotate-0 scale-100 opacity-100',
        )}
      />
    </IconButton>
  );
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'relative rounded-lg px-3 py-2 text-sm font-medium transition duration-300',
    isActive ? 'text-accent' : 'text-muted hover:bg-sunken hover:text-body',
  );

/**
 * Whether the current URL sits inside a section.
 *
 * Which entry is lit is worked out from the section it stands for rather than left to `NavLink`.
 * `NavLink` decides from the destination, and several entries share a destination when nobody is
 * signed in — everything behind an account leads to the login page. Judged that way, the login page
 * lit two or three entries at once, and a bar with several current entries reads as a broken bar.
 */
const inSection = (pathname: string, section: string) =>
  pathname === section || pathname.startsWith(`${section}/`);

function DesktopNav() {
  const { user } = useAuth();
  const unread = useUnreadMessages();

  return (
    <nav className="ml-3 hidden items-center gap-0.5 md:flex">
      <NavLink to="/" end viewTransition className={navLinkClass}>
        {({ isActive }) => (
          <>
            Pretraga
            <ActiveUnderline show={isActive} />
          </>
        )}
      </NavLink>
      {user && (
        <>
          <NavLink to="/moji-oglasi" viewTransition className={navLinkClass}>
            {({ isActive }) => (
              <>
                Moji oglasi
                <ActiveUnderline show={isActive} />
              </>
            )}
          </NavLink>
          <NavLink to="/sacuvani" viewTransition className={navLinkClass}>
            {({ isActive }) => (
              <>
                Sačuvani
                <ActiveUnderline show={isActive} />
              </>
            )}
          </NavLink>
          <NavLink to="/poruke" viewTransition className={navLinkClass}>
            {({ isActive }) => (
              <>
                Poruke
                {unread > 0 && (
                  <span className="animate-pop-in ml-1.5 inline-flex size-4 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-on-accent">
                    {unread}
                  </span>
                )}
                <ActiveUnderline show={isActive} />
              </>
            )}
          </NavLink>
        </>
      )}
    </nav>
  );
}

function ActiveUnderline({ show }: { show: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'absolute inset-x-3 -bottom-0.5 h-0.5 origin-center rounded-full bg-accent',
        'transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
        show ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0',
      )}
    />
  );
}

function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const link = useGuardedLink();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // The header is transparent over the hero and gains its glass and hairline only once the page has
  // moved. A permanently opaque bar makes a landing page feel like an admin panel.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /*
    Closed on any navigation, keyed on the history entry rather than the path. Keyed on the path, a
    tap on an entry leading where the visitor already is changed nothing React could see, so the
    menu stayed open over a page that had not moved and looked stuck.
  */
  useEffect(() => setMenuOpen(false), [location.key]);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <header
      className={cn(
        'site-header sticky top-0 z-40 transition duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
        scrolled
          ? 'glass border-b border-default shadow-[0_1px_0_0_var(--hairline)]'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4">
        <Wordmark />
        <DesktopNav />

        <div className="ml-auto flex items-center gap-1.5">
          <NotificationBell />
          <ThemeToggle />

          <Link {...link('/postavi-oglas')} className="hidden sm:block">
            <Button size="sm">
              <Plus size={16} />
              Postavi oglas
            </Button>
          </Link>

          {user ? (
            <div className="hidden items-center gap-1.5 md:flex">
              {/* The name is the way into the profile: an obvious place to look, and one fewer item
                  competing for space in the navigation. */}
              <Link
                to="/profil"
                viewTransition
                className="max-w-32 truncate rounded-lg px-2 py-1 text-sm text-muted transition hover:bg-sunken hover:text-body"
              >
                {user.displayName}
              </Link>
              <Link to="/podesavanja" viewTransition>
                <IconButton label="Podešavanja">
                  <Settings size={17} />
                </IconButton>
              </Link>
              <IconButton label="Odjavi se" onClick={handleLogout}>
                <LogOut size={17} />
              </IconButton>
            </div>
          ) : (
            <Link {...link('/prijava')} className="hidden md:block">
              <Button variant="secondary" size="sm">
                <UserIcon size={16} />
                Prijava
              </Button>
            </Link>
          )}

          <IconButton
            label="Meni"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
            className="md:hidden"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </IconButton>
        </div>
      </div>

      {menuOpen && (
        <div className="animate-fade-in-down glass border-t border-default px-4 py-3 md:hidden">
          <nav className="flex flex-col gap-0.5">
            <NavLink to="/" end viewTransition className={navLinkClass}>
              Pretraga
            </NavLink>
            {/*
              A `Link` rather than a `NavLink`: signed out this leads to the login page, and letting
              the destination decide would light it alongside "Prijava" directly beneath it.
            */}
            <Link
              {...link('/postavi-oglas')}
              className={navLinkClass({
                isActive: inSection(location.pathname, '/postavi-oglas'),
              })}
            >
              Postavi oglas
            </Link>
            {user ? (
              <>
                <NavLink to="/moji-oglasi" viewTransition className={navLinkClass}>
                  Moji oglasi
                </NavLink>
                <NavLink to="/sacuvani" viewTransition className={navLinkClass}>
                  Sačuvani oglasi
                </NavLink>
                <NavLink to="/poruke" viewTransition className={navLinkClass}>
                  Poruke
                </NavLink>
                <NavLink to="/profil" viewTransition className={navLinkClass}>
                  Moj profil
                </NavLink>
                <NavLink to="/podesavanja" viewTransition className={navLinkClass}>
                  Podešavanja
                </NavLink>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="rounded-lg px-3 py-2 text-left text-sm font-medium text-danger transition hover:bg-sunken"
                >
                  Odjavi se
                </button>
              </>
            ) : (
              <NavLink {...link('/prijava')} className={navLinkClass}>
                Prijava
              </NavLink>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

/**
 * The phone navigation bar.
 *
 * A thumb reaches the bottom of a 6.7" screen and does not reach the top, and the four things
 * anyone does here — search, save, post, account — fit in one row. The old design had a floating
 * button for posting and nothing else, so getting back to saved cars meant scrolling to the header.
 */
function MobileTabBar() {
  const { user } = useAuth();
  const unread = useUnreadMessages();
  const { pathname } = useLocation();
  const link = useGuardedLink();

  const tab = (isActive: boolean) =>
    cn(
      'flex flex-1 flex-col items-center justify-center gap-1 py-2 text-[10.5px] font-medium transition duration-300',
      isActive ? 'text-accent' : 'text-faint',
    );

  /*
    Lit by the section the tab stands for, not by where tapping it leads today — see `inSection`.
    Signed out, three of these five lead to the login page.
  */
  const onSearch = pathname === '/';
  const onMessages = inSection(pathname, '/poruke');
  const onFavourites = inSection(pathname, '/sacuvani');
  const onAccount = user ? inSection(pathname, '/moji-oglasi') : inSection(pathname, '/prijava');

  return (
    <nav
      aria-label="Glavna navigacija"
      className="tab-bar glass fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-default sm:hidden"
    >
      <Link {...link('/')} aria-current={onSearch ? 'page' : undefined} className={tab(onSearch)}>
        <Search size={19} className={cn('transition duration-300', onSearch && 'scale-110')} />
        Pretraga
      </Link>

      <Link
        {...link('/poruke')}
        aria-current={onMessages ? 'page' : undefined}
        className={tab(onMessages)}
      >
        <span className="relative">
          <MessagesSquare
            size={19}
            className={cn('transition duration-300', onMessages && 'scale-110')}
          />
          {user && unread > 0 && (
            <span className="animate-pop-in absolute -top-1 -right-1.5 flex size-3.5 items-center justify-center rounded-full bg-accent text-[8px] font-bold text-on-accent">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
        Poruke
      </Link>

      {/* The one raised control. Posting an ad is the only thing a seller comes here to do. */}
      <div className="tab-bar-raised flex w-20 shrink-0 items-start justify-center">
        <Link
          {...link('/postavi-oglas')}
          aria-label="Postavi oglas"
          className="press -mt-5 flex size-14 items-center justify-center rounded-full bg-accent text-on-accent shadow-[inset_0_1px_0_0_rgb(255_255_255/0.25),0_10px_24px_-8px_var(--accent)]"
        >
          <Plus size={24} />
        </Link>
      </div>

      <Link
        {...link('/sacuvani')}
        aria-current={onFavourites ? 'page' : undefined}
        className={tab(onFavourites)}
      >
        <Heart
          size={19}
          className={cn('transition duration-300', onFavourites && 'scale-110 fill-current')}
        />
        Sačuvani
      </Link>

      <Link
        {...link('/moji-oglasi')}
        aria-current={onAccount ? 'page' : undefined}
        className={tab(onAccount)}
      >
        <UserIcon size={19} className={cn('transition duration-300', onAccount && 'scale-110')} />
        {user ? 'Oglasi' : 'Prijava'}
      </Link>
    </nav>
  );
}

/**
 * The footer, and the site's internal link graph.
 *
 * The marque links are not decoration. Every category page the server puts in the sitemap needs a
 * path a crawler can walk to from any page on the site, and on a marketplace whose every other link
 * points at a single advert this is the only place that provides one. The list is short and ordered
 * by stock, so it stays a set of useful links rather than a wall of them.
 */
function Footer() {
  const link = useGuardedLink();
  const { data: catalogue } = useSeoCatalogue();

  const makes = (catalogue?.makes ?? []).slice(0, 10);

  return (
    <footer className="relative mt-20 overflow-hidden border-t border-default bg-sunken">
      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-sm">
            <Wordmark />
            <p className="mt-3 text-sm leading-relaxed text-muted">
              Besplatan oglasnik za polovne automobile. Bez reklama, bez naplate, zauvek.
            </p>
          </div>

          {/*
            Every internal link here goes through the guard, including the ones that are not gated.
            It is not only about "Postavi oglas" sending a signed-out visitor to the login page in
            one navigation rather than two — it is also what makes tapping a link to the page you are
            already on scroll to the top instead of pushing a duplicate history entry.
          */}
          <div className="grid grid-cols-2 gap-x-10 gap-y-6 sm:grid-cols-2">
            <nav aria-labelledby="footer-nav" className="flex flex-col gap-2 text-sm text-muted">
              {/*
                A paragraph rather than a heading. It labels a navigation block, not a section of
                the document, and putting "KojaKola" and "Po marki" into the heading outline of
                every page on the site buries the one heading on each that is meant to be read.
              */}
              <p id="footer-nav" className="text-xs font-semibold tracking-wide text-faint uppercase">
                KojaKola
              </p>
              <Link {...link('/')} className="transition hover:text-accent">
                Pretraga
              </Link>
              <Link {...link(categoryPath())} className="transition hover:text-accent">
                Polovni automobili
              </Link>
              <Link {...link(`/${BUY_GUIDE_SLUG}`)} className="transition hover:text-accent">
                Kupujem auto
              </Link>
              <Link {...link(`/${SELL_GUIDE_SLUG}`)} className="transition hover:text-accent">
                Prodajem auto
              </Link>
              <Link {...link('/postavi-oglas')} className="transition hover:text-accent">
                Postavi oglas
              </Link>
              <Link {...link('/sacuvani')} className="transition hover:text-accent">
                Sačuvani oglasi
              </Link>
              <a href="mailto:podrska@kojakola.com" className="transition hover:text-accent">
                Podrška
              </a>
              <Link {...link('/privatnost')} className="transition hover:text-accent">
                Privatnost
              </Link>
              <Link {...link('/uslovi')} className="transition hover:text-accent">
                Uslovi korišćenja
              </Link>
            </nav>

            {makes.length > 0 && (
              <nav aria-labelledby="footer-makes" className="flex flex-col gap-2 text-sm text-muted">
                <p
                  id="footer-makes"
                  className="text-xs font-semibold tracking-wide text-faint uppercase"
                >
                  Po marki
                </p>
                {makes.map((make) => (
                  <Link
                    key={make.slug}
                    {...link(categoryPath(make.slug))}
                    className="transition hover:text-accent"
                  >
                    Polovni {make.name}
                  </Link>
                ))}
              </nav>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-default px-4 py-4">
        <p className="mx-auto max-w-7xl text-xs text-faint">
          © {new Date().getFullYear()} KojaKola — besplatni oglasi za polovne automobile u Srbiji
        </p>
      </div>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />

      {/*
        Clears the fixed tab bar so the footer is never trapped underneath it. Measured from the
        same token the bar sizes itself with, rather than from a number that happens to match it.
      */}
      <div aria-hidden className="h-[var(--tab-bar-total)] sm:hidden" />
      <MobileTabBar />
    </div>
  );
}
